import requestPortalFormMethods from './request_portal_form_methods';
import requestPortalCaseMethods from './request_portal_case_methods';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import ConfiguredCaseForm from '@/components/x-verdict/ConfiguredCaseForm.vue';
import {
  is_active_case_status,
  normalize_text,
  read_session_project_id,
  to_number,
} from './request_portal_helpers';
import {
  can_refresh_open_cases,
  ordered_sections,
  section_cards,
} from './request_portal_section_helpers';

function previous_case_meta(case_item = {}) {
  return case_item.meta || `${case_item.type_label || 'Case'} - opened ${case_item.opened}`;
}

function previous_case_date(case_item = {}) {
  return case_item.date || case_item.opened;
}

function previous_case_record(case_item = {}) {
  return {
    ...case_item,
    meta: previous_case_meta(case_item),
    tone: case_item.tone || '',
    date: previous_case_date(case_item),
  };
}

function active_case_record(case_item = {}) {
  return is_active_case_status(case_item);
}

function previous_case_record_is_visible(case_item = {}) {
  return !active_case_record(case_item);
}

function previous_case_about_text(case_item = {}) {
  return normalize_text(first_available_case_text(case_item));
}

function first_available_case_text(case_item = {}) {
  return [
    case_item?.entity_profile_name,
    case_item?.title,
    case_item?.update_body,
    'No case summary was found.',
  ].find(Boolean);
}

export default {
  name: 'RequestorPortal',
  components: {
    BlockingLoaderOverlay,
    ConfiguredCaseForm,
  },
  data: () => ({
    active_tab: 'active',
    show_verify_modal: false,
    show_new_case_modal: false,
    loading_case_types: false,
    loading_case_form: false,
    loading_open_cases: false,
    submitting_new_case: false,
    portal_error: '',
    modal_error: '',
    selected_document: null,
    replacing_document: false,
    document_replace_error: '',
    document_replace_success: '',
    uploading_case_document: false,
    selected_upload_case_id: null,
    case_document_upload_error: '',
    case_document_upload_success: '',
    selected_previous_case: null,
    validation_errors: [],
    backend_open_cases: [],
    collapsed_case_ids: [],
    case_type_options: [],
    selected_entity_type_id: null,
    selected_form_id: null,
    selected_form_details: {},
    answer_values: [],
    editing_case_section_key: '',
    editing_case_answers: [],
    saving_case_section_key: '',
    case_section_edit_error: '',
    case_section_edit_success: '',
    last_open_cases_refresh_at: 0,
    verify_id_number: '9001015001087',
    verify_contact_channel: 'sms',
  }),
  computed: {
    project_id() {
      return to_number(
        this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id()
      );
    },
    requestor_name() {
      const name = String(this.$store.getters.currentLeadName || 'Joseph').trim();
      return `Hi ${name}.`;
    },
    requestor_initials() {
      const name = String(this.$store.getters.currentLeadName || 'Joseph Mokwena').trim();
      return name
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase() || 'JM';
    },
    policy_number() {
      return 'PM-883441';
    },
    member_since() {
      return 'Mar 2022';
    },
    active_cases() {
      return this.backend_open_cases.filter(active_case_record);
    },
    active_case_count() {
      return this.active_cases.length;
    },
    previous_case_count() {
      return this.previous_cases.length;
    },
    active_cases_summary() {
      if (this.loading_open_cases) {
        return 'Loading cases';
      }
      return `${this.active_case_count} active`;
    },
    previous_cases() {
      return this.backend_open_cases
        .filter(previous_case_record_is_visible)
        .map(previous_case_record);
    },
    show_previous_case_modal() {
      return Boolean(this.selected_previous_case);
    },
    previous_case_about() {
      return previous_case_about_text(this.selected_previous_case);
    },
    previous_case_fact_grid() {
      const case_facts = Array.isArray(this.selected_previous_case?.case_facts)
        ? this.selected_previous_case.case_facts
        : [];

      return case_facts.filter((fact) => normalize_text(fact?.value) && !fact?.document).slice(0, 6);
    },
    previous_case_documents() {
      if (!this.selected_previous_case) {
        return [];
      }

      const existing_documents = new Map();
      [...(this.selected_previous_case.documents || [])].forEach((document) => {
        existing_documents.set(document.id || document.title, document);
      });

      (this.selected_previous_case.case_fact_sections || [])
        .flatMap((section) => Array.isArray(section?.facts) ? section.facts : [])
        .map((fact) => fact?.document)
        .filter(Boolean)
        .forEach((document) => {
          existing_documents.set(document.id || document.title, document);
        });

      return Array.from(existing_documents.values());
    },
    case_types() {
      return this.case_type_options;
    },
    selected_case_type() {
      return this.case_type_options.find((item) => Number(item.id) === Number(this.selected_entity_type_id)) || null;
    },
    ordered_sections() {
      return ordered_sections(this.selected_form_details);
    },
    section_cards() {
      return section_cards(this.ordered_sections);
    },
    required_questions() {
      return this.section_cards.flatMap((section) =>
        (section.questions || []).filter((question) => this.is_question_required(question))
      );
    },
    visible_questions() {
      return this.section_cards.flatMap((section) => section.questions || []);
    },
    has_any_answered_questions() {
      if (!this.visible_questions.length) {
        return false;
      }

      return this.visible_questions.some((question) => {
        const answer = this.answer_value_by_key(this.question_key(question));
        return !this.is_answer_empty(question, answer);
      });
    },
    has_completed_required_questions() {
      if (!this.required_questions.length) {
        return true;
      }

      return this.required_questions.every((question) => {
        const answer = this.answer_value_by_key(this.question_key(question));
        return !this.is_answer_empty(question, answer);
      });
    },
    can_submit_new_case() {
      return Boolean(
        this.selected_entity_type_id
        && this.selected_form_id
        && this.section_cards.length
        && this.has_any_answered_questions
        && this.has_completed_required_questions
      );
    },
    show_document_drawer() {
      return Boolean(this.selected_document);
    },
    page_loading() {
      return this.loading_open_cases
        || this.loading_case_types
        || this.loading_case_form
        || this.submitting_new_case
        || Boolean(this.saving_case_section_key);
    },
    page_loading_message() {
      if (this.submitting_new_case) {
        return 'Opening your case...';
      }

      if (this.saving_case_section_key) {
        return 'Saving case details...';
      }

      if (this.loading_case_form) {
        return 'Loading case form...';
      }

      if (this.loading_case_types) {
        return 'Loading case types...';
      }

      return 'Loading your cases...';
    },
    page_loading_detail() {
      if (this.submitting_new_case) {
        return 'Please wait while we create your case and submit the answers.';
      }

      if (this.saving_case_section_key) {
        return 'Please wait while we update your submitted case information.';
      }

      if (this.loading_case_form || this.loading_case_types) {
        return 'Preparing the form fields for this request.';
      }

      return 'Pulling the latest case information from the backend.';
    },
  },
  async created() {
    await this.initialize_portal();
  },
  mounted() {
    window.addEventListener('focus', this.handle_portal_focus);
    document.addEventListener('visibilitychange', this.handle_visibility_change);
  },
  beforeUnmount() {
    window.removeEventListener('focus', this.handle_portal_focus);
    document.removeEventListener('visibilitychange', this.handle_visibility_change);
  },
  methods: {
    update_configured_form_answer({ question, value } = {}) {
      this.update_answer_value(question, value);
    },
    scroll_new_case_feedback_into_view() {
      this.$nextTick(() => {
        const feedback = this.$refs.newCaseError;
        const firstInvalidField = this.$el?.querySelector?.('.requestor-portal-modal .is-invalid');
        const target = feedback || firstInvalidField;

        if (!target || typeof target.scrollIntoView !== 'function') {
          return;
        }

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (feedback && typeof feedback.focus === 'function') {
          feedback.focus({ preventScroll: true });
        }
      });
    },
    open_verify_modal() {
      this.show_verify_modal = true;
    },
    close_verify_modal() {
      this.show_verify_modal = false;
    },
    async refresh_open_cases_if_visible() {
      if (!can_refresh_open_cases(this)) {
        return;
      }

      this.last_open_cases_refresh_at = Date.now();
      await this.load_open_cases();
    },
    handle_portal_focus() {
      this.refresh_open_cases_if_visible();
    },
    handle_visibility_change() {
      if (!document.hidden) {
        this.refresh_open_cases_if_visible();
      }
    },
    ...requestPortalFormMethods,
    ...requestPortalCaseMethods,
  },
};
